import { isHuafenda24GKeyboard } from "../main/deviceConnectionUtils";
import { resolveHuafendaMusicAmplitude } from "./audioFrameBuilder";
import type { RhythmKeyColor } from "./buildRhythmMusicData";
import { resolveHfdLightingVersion } from "./hfdSdkRuntime";
import type { RhythmLayoutKey } from "./rhythmLayoutKeys";

/** X87 / SMART875 等旧 2.4G（lightingVersion=0）：逐键 RGB 组包，非 USB keyIndex 排序 */
export function isHfdLegacy24GRhythmTarget(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  return (
    isHuafenda24GKeyboard(equipment) &&
    resolveHfdLightingVersion(equipment) !== 1
  );
}

/**
 * 华奋达 setMusicData 振幅：
 * - 旧 2.4G：全局 amplitude 会使未写入律动的灯位随节拍闪，仅下发逐键 RGB（亮度已编码在色值里）
 * - 有线 / 新 2.4G：保持 1~7 整数振幅（对齐 ai-hub）
 */
export function resolveHfdRhythmHardwareAmplitude(
  equipment: Record<string, unknown> | null | undefined,
  rms: number,
  fallback: number,
  rhythmActive: boolean
): number {
  if (!rhythmActive) return 0;
  if (isHfdLegacy24GRhythmTarget(equipment)) return 0;
  return resolveHuafendaMusicAmplitude(rms, fallback);
}

/** 经 normalizeHardwareRgb 后仍会在实体上可见的最低阈值 */
export function isActiveHfdRhythmHardwareColor(
  color: { R: number; G: number; B: number } | undefined
): boolean {
  if (!color) return false;
  const sum = color.R + color.G + color.B;
  if (sum < 24) return false;
  return Math.max(color.R, color.G, color.B) >= 68;
}

/**
 * 旧 2.4G 布局序组包：非律动键强制全黑，避免低亮度残色 + 全局振幅在实体上误闪。
 */
export function clampHfdRhythmKeyColorsForLegacy24G(
  keyColors: Record<string, RhythmKeyColor>,
  rhythmKeys: RhythmLayoutKey[]
): Record<string, RhythmKeyColor> {
  const next = { ...keyColors };
  for (const key of rhythmKeys) {
    if (!isActiveHfdRhythmHardwareColor(next[key.code])) {
      next[key.code] = { R: 0, G: 0, B: 0, isCustom: false };
    }
  }
  return next;
}
